import { Component, DestroyRef, inject, OnInit, signal, WritableSignal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { Observable, catchError, of } from 'rxjs';
import { PipingAndValvesDocumentService } from '../../piping-valves/services/piping-and-valves-document.service';
import { ApiException, EsraPipingAPIClient, FileResponse, ResolveShareLinkResponse, ShareLinkClass } from '../../shared/models/autogenerated-piping';

/**
 * Route segment for the summary page of each share-linkable object type, keyed by `ShareLinkClass`.
 * `Record<ShareLinkClass, string>` forces a compile error here whenever the backend adds a new
 * `ShareLinkClass` value, so this map can't silently fall out of sync with the API contract.
 */
const SUMMARY_ROUTE_BY_SHARE_LINK_CLASS: Record<ShareLinkClass, string> = {
  [ShareLinkClass.PipeClass]: 'pipingsummary',
  [ShareLinkClass.Valve]: 'valvesummary',
  // PDF types are opened as documents via openPdfDocument -- they do not navigate to a summary route.
  [ShareLinkClass.PdfPipeClass]: '',
  [ShareLinkClass.PdfValve]: '',
};

const INVALID_LINK_MESSAGE: string = 'This link is invalid or has expired.';

/**
 * Resolves short permalinks of the form `/p/<businessUnit>/<name>` (pipe class)
 * and `/v/<businessUnit>/<tag>` (valve) into the object's real summary page.
 * Sends the matched URL path as-is to `ShareLinkResolve/resolve` and redirects using the
 * returned `shareLinkClass` + `objectId`.
 */
@Component({
    selector: 'app-share-link-resolver',
    templateUrl: './share-link-resolver.component.html',
    styleUrls: ['./share-link-resolver.component.scss'],
    standalone: false
})
export class ShareLinkResolverComponent implements OnInit {
  private readonly pipingApiClient: EsraPipingAPIClient = inject(EsraPipingAPIClient);
  private readonly router: Router = inject(Router);
  private readonly documentService: PipingAndValvesDocumentService = inject(PipingAndValvesDocumentService);
  private readonly destroyRef: DestroyRef = inject(DestroyRef);

  readonly loading: WritableSignal<boolean> = signal<boolean>(true);
  readonly errorMessage: WritableSignal<string | undefined> = signal<string | undefined>(undefined);
  readonly documentAction: WritableSignal<'opened' | 'downloaded' | undefined> = signal<'opened' | 'downloaded' | undefined>(undefined);

  ngOnInit(): void {
    // The matched route's path segments ARE the permalink, e.g. "p/CES/12DF01".
    const shareLink: string = this.router.url.split(/[?#]/)[0].replace(/^\/+/, '');

    this.pipingApiClient.resolveShareLink(shareLink).pipe(
      takeUntilDestroyed(this.destroyRef),
      catchError((error: unknown): Observable<undefined> => {
        this.errorMessage.set(this.extractErrorMessage(error));
        this.loading.set(false);
        return of(undefined);
      })
    ).subscribe((response: ResolveShareLinkResponse | undefined) => {
      if (response) {
        this.redirectToSummaryPage(response);
      }
    });
  }

  /** Navigates to the resolved object's summary page, or fetches and opens the PDF for PDF link types. */
  private redirectToSummaryPage(response: ResolveShareLinkResponse): void {
    if (response.shareLinkClass === undefined || !response.objectId) {
      this.errorMessage.set(INVALID_LINK_MESSAGE);
      this.loading.set(false);
      return;
    }

    if (response.shareLinkClass === ShareLinkClass.PdfPipeClass) {
      this.openPdfDocument('pp', response.objectId);
      return;
    }
    if (response.shareLinkClass === ShareLinkClass.PdfValve) {
      this.openPdfDocument('pv', response.objectId);
      return;
    }

    const routeSegment: string = SUMMARY_ROUTE_BY_SHARE_LINK_CLASS[response.shareLinkClass];
    this.router.navigate([routeSegment, response.objectId], { replaceUrl: true });
  }

  /** Fetches the ESRA1 document for the resolved record ID; PDFs open in a new tab, other formats download. */
  private openPdfDocument(typeCode: 'pp' | 'pv', objectId: string): void {
    this.pipingApiClient.getEsra1DocumentByRecordId(
      typeCode,
      objectId
    ).pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: (fileResponse: FileResponse) => {
        this.documentAction.set(this.documentService.openFileResponse(fileResponse));
        this.loading.set(false);
      },
      error: (error: unknown) => {
        this.errorMessage.set(this.extractErrorMessage(error));
        this.loading.set(false);
      },
    });
  }

  /**
   * Extracts a user-readable message from a failed `resolveShareLink` call. The generated client
   * throws the raw response body (a plain string) when the backend returns one, or an
   * `ApiException` otherwise -- see `throwException()` in `autogenerated-piping.ts`.
   */
  private extractErrorMessage(error: unknown): string {
    if (typeof error === 'string' && error) {
      return error;
    }
    if (ApiException.isApiException(error) && error.response) {
      return error.response;
    }
    return INVALID_LINK_MESSAGE;
  }
}
